import { message } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import useGetShoppingCartQuery from '../../../../queries/useGetShoppingCartQuery';
import useAuthStore from '../../../../store/authStore';
import useDatasetStore from '../../../../store/datasetStore';
import useHomeStore from '../../../../store/homeStore';
import { useActiveMarker } from '../../../../store/markerStore';
import { usePointStore } from '../../../../store/pointStore';
import { DownloadInfoItem } from '../../../../types/analytics';
import {
  EVENT_DATA_ADDED_TO_CART,
  trackUpdate,
} from '../../../../utils/matomoEvents';
import { getPointNameCartError } from '../component/Modal/Error/PointNameInCartError';

export function useDatasetCart(
  item: DownloadInfoItem,
  coords: number[],
  label: string,
  namePanel: string,
  price: number,
  isDTU: boolean,
  onAddProductToCart: (
    item: DownloadInfoItem,
    pointName: string,
    category: string,
    coordinates: number[]
  ) => Promise<boolean>,
  onRemoveProductFromCart: (pointName: string, category: string) => void
) {
  const [parameterCartLoading, setParameterCartLoading] =
    useState<boolean>(false);
  const [spectraCartLoading, setSpectraCartLoading] = useState<boolean>(false);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setShowLogin = useHomeStore((state) => state.setShowLogin);
  const timeStepInterval = useHomeStore((state) => state.timeStepInterval);

  const point = useActiveMarker();
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const fwePoints = usePointStore((state) => state.fwePoints);

  const { data } = useGetShoppingCartQuery();
  const shoppingItems = useMemo(
    () => data?.shoppingItems || {},
    [data?.shoppingItems]
  );

  const isCategoryInCart = useCallback(
    (categoryName: string): boolean => {
      if (!point || !point.label) return false;

      const shoppingItemsOfPoint = shoppingItems?.[point.label];
      if (!shoppingItemsOfPoint) return false;

      return shoppingItemsOfPoint[categoryName] !== undefined;
    },
    [point, shoppingItems]
  );

  const isItemInCart = useCallback((): boolean => {
    if (!point || !point.label) return false;

    const shoppingItemsOfPoint = shoppingItems?.[point.label];
    if (!shoppingItemsOfPoint) return false;

    const category = shoppingItemsOfPoint[label];
    if (!category) return false;

    const sameDataset = category.dataset_id === item.datasetId;
    if (!sameDataset) return false;

    // Also check coordinates to distinguish from points with same name
    return category.location.replace(' ', '') === point.coordinates.join();
  }, [point, shoppingItems, item, label]);

  const addedToCart = isItemInCart();
  const spectraAddedToCart = isCategoryInCart('Spectra');

  const addToCart = useCallback(async () => {
    if (!point?.label) {
      message.warning('Please give the active point a name');
      return;
    }

    setParameterCartLoading(true);

    const pointIsInCard = isItemInCart();

    try {
      if (pointIsInCard) {
        message.error(getPointNameCartError(point.label));
      } else {
        const isAddedToCart = await onAddProductToCart(
          item,
          point.label,
          label,
          coords
        );

        if (isAddedToCart) {
          trackUpdate(EVENT_DATA_ADDED_TO_CART, coords, item.datasetId, '');
        }
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      message.error('Failed to prepare data');
    } finally {
      setParameterCartLoading(false);
    }
  }, [coords, isItemInCart, item, label, onAddProductToCart, point?.label]);

  const addSpectraToCart = useCallback(async () => {
    if (!point?.label) {
      message.warning('Please give the active point a name');
      return;
    }

    setSpectraCartLoading(true);

    try {
      if (isCategoryInCart('Spectra')) {
        message.error(getPointNameCartError(point.label));
      } else {
        const isAddedToCart = await onAddProductToCart(
          item,
          point.label,
          'Spectra',
          coords
        );

        if (isAddedToCart) {
          trackUpdate(EVENT_DATA_ADDED_TO_CART, coords, item.datasetId, '');
        }
      }
    } catch (error) {
      console.error('Error adding spectra to cart:', error);
      message.error('Failed to prepare data');
    } finally {
      setSpectraCartLoading(false);
    }
  }, [point, item, coords, onAddProductToCart, isCategoryInCart]);

  const handleAddToCart = useCallback((): void => {
    if (!isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    addToCart();
  }, [isAuthenticated, addToCart, setShowLogin]);

  const removeFromCart = useCallback(async () => {
    if (point === undefined || !point?.label) {
      return;
    }

    setParameterCartLoading(true);
    await onRemoveProductFromCart(point?.label, label);
    setParameterCartLoading(false);
  }, [label, onRemoveProductFromCart, point]);

  const removeSpectraFromCart = useCallback(async () => {
    if (point === undefined || !point?.label) {
      return;
    }

    setSpectraCartLoading(true);
    await onRemoveProductFromCart(point?.label, 'Spectra');
    setSpectraCartLoading(false);
  }, [onRemoveProductFromCart, point]);

  const handleAddSpectraToCart = useCallback((): void => {
    if (!isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    addSpectraToCart();
  }, [isAuthenticated, setShowLogin, addSpectraToCart]);

  const handleRemoveFromCart = useCallback((): void => {
    if (!isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    removeFromCart();
  }, [isAuthenticated, removeFromCart, setShowLogin]);

  const handleRemoveSpectraFromCart = useCallback((): void => {
    if (!isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    removeSpectraFromCart();
  }, [isAuthenticated, removeSpectraFromCart, setShowLogin]);

  return {
    parameterCartLoading,
    spectraCartLoading,
    addedToCart,
    spectraAddedToCart,
    handleAddToCart,
    handleAddSpectraToCart,
    handleRemoveFromCart,
    handleRemoveSpectraFromCart,
  };
}
